import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IDLE_CONTROL } from '../config-control';
import { CONSULT_MODE, NONE_MODE } from '../mode';
import { ALTERNATIVE_LOADING_STATUS, FAILURE_STATUS, IDLE_STATUS, LOADING_STATUS, SUCCESS_STATUS } from '../status';
import {
  activateById,
  fetchAll,
  fetchByFilter,
  fetchById,
  fetchNCMsByFilterPagination,
  inactivateById,
  fetchAtributosNcmByCode,
} from './ncmSubitemAPI';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  error: null,
  mode: {
    main: NONE_MODE,
  },
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],
  config: { control: IDLE_CONTROL },
  model: {
    id: '',
    code: '',
    description: '',
    active: null,
  },
  metadadosAtributos: [],
  listaAtributosMetadados: [],
};


const fetchListaAtributosMetadados = createAsyncThunk('ncmSubitem/fetchListaAtributosMetadados', async (code) => {
  if (!code) return []
  const { data } = await fetchAtributosNcmByCode(code);
  return { data };
});

const fetchAllAsync = createAsyncThunk('ncmSubitem/fetchAll', async () => {
  const { data } = await fetchAll();
  return { data };
});

const fetchByFilterAsync = createAsyncThunk('ncmSubitem/fetchByFilter', async (params) => {
  const { data } = await fetchByFilter(params);
  return { data };
});
const fetchByIdAsync = createAsyncThunk('ncmSubitem/fetchById', async (data) => {
  const response = await fetchById(data);
  return {
    response: {
      status: response.status,
      data: response.data,
    },
  };
});

const activateByIdAsync = createAsyncThunk('ncmSubitem/activateById', async (id) => {
  const { status, data } = await activateById(id);
  return { response: { status, data } };
});

const inactivateByIdAsync = createAsyncThunk('ncmSubitem/inactivateById', async (id) => {
  const { status, data } = await inactivateById(id);
  return { response: { status, data } };
});

const fetchNCMsByFilterPaginationAsync = createAsyncThunk('ncmSubitem/fetchByFilterPagination', async (params) => {
  const { data } = await fetchNCMsByFilterPagination(params);
  return { data };
});

const ncmSubitemSlice = createSlice({
  name: 'ncmSubitem',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    changeControlTo: (state, action) => {
      state.config.control = action.payload;
    },
    resetList: (state) => {
      state.list = [];
    },
    setMetadadosAtributos: (state, action) => {
      state.metadadosAtributos = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
    
      .addCase(fetchListaAtributosMetadados.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchListaAtributosMetadados.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.listaAtributosMetadados = action.payload.data;
      })
      .addCase(fetchListaAtributosMetadados.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarListaNCMs', { erro: action.error.message }),
        };
      })
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarListaNCMs', { erro: action.error.message }),
        };
      })
      .addCase(fetchByFilterAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchByFilterAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchByFilterAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarListaNCMs', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = action.payload.response.data;
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarDadosNCM', { erro: action.error.message }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.NCMativado', { ncm: description });

        if (state.response.status === 200) {
          state.list = state.list.map((current) =>
            current.id === action.payload.response.data.id ? action.payload.response.data : current
          );
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.ativarNCM', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.NCMinativado', { ncm: description });

        if (state.response.status === 200) {
          state.list = state.list.map((current) =>
            current.id === action.payload.response.data.id ? action.payload.response.data : current
          );
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.inativarNCM', { erro: action.error.message }),
        };
      })
      .addCase(fetchNCMsByFilterPaginationAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchNCMsByFilterPaginationAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action?.payload?.data?.itens;
        state.count = action?.payload?.data?.totalNumberOfItens;
      })
      .addCase(fetchNCMsByFilterPaginationAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.carregarListaNCMs', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  addToList,
  updateOnList,
  setResponse,
  setResponseMessage,
  setMetadadosAtributos,
  clearResponse,
  setError,
  resetModel,
  changeControlTo,
  setConfig,
  resetList,
  changeToConsultMode,
} = ncmSubitemSlice.actions;

const selectMetadadosAtributos = (state) => state.ncmSubitem.metadadosAtributos;
const selectNcmSubitemsCount = (state) => state.ncmSubitem.count;
const selectNcmSubitens = (state) => state.ncmSubitem.list;
const selectStatus = (state) => state.ncmSubitem.status;
const selectError = (state) => state.ncmSubitem.error;
const selectResponse = (state) => state.ncmSubitem.response;
const selectMode = (state) => state.ncmSubitem.mode.main;
const selectNcmSubitem = (state) => state.ncmSubitem.model;
const selectConfigControl = (state) => state.ncmSubitem.config.control;
const selectListaAtributosMetadados = (state) => state.ncmSubitem.listaAtributosMetadados;

const ncmSubitemActions = {
  ...ncmSubitemSlice.actions,
  fetchAllAsync,
  fetchByFilterAsync,
  fetchByIdAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  fetchNCMsByFilterPaginationAsync,
  selectNcmSubitens,
  selectMetadadosAtributos,
  selectStatus,
  selectError,
  selectResponse,
  selectNcmSubitem,
  selectConfigControl,
  selectMode,
  selectNcmSubitemsCount,
  selectListaAtributosMetadados,
  fetchListaAtributosMetadados,
};

export {
  ncmSubitemSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  addToList,
  selectMode,
  updateOnList,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  resetModel,
  fetchAllAsync,
  fetchByFilterAsync,
  fetchByIdAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  changeControlTo,
  setConfig,
  ncmSubitemActions,
  resetList,
  setMetadadosAtributos,
  selectNcmSubitens,
  selectNcmSubitem,
  selectStatus,
  selectError,
  selectResponse,
  selectConfigControl,
  changeToConsultMode,
  selectNcmSubitemsCount,
  selectMetadadosAtributos,
  fetchNCMsByFilterPaginationAsync,
  fetchListaAtributosMetadados,
  selectListaAtributosMetadados,
};

export default ncmSubitemSlice.reducer;
